/**
 * Music Theory Module - Public API
 *
 * Point d'entree des donnees de theorie musicale.
 *
 * Socle S0 : les donnees (MODES, derives, moods) viennent du MIROIR vivant
 * (src/data/contentStore), hydrate depuis le content pack (bundle au boot, puis
 * deltas IndexedDB). Les constantes/fonctions non-contenu restent dans modes.js.
 */

// ── Donnees vives depuis le miroir ──
import {
  MODES,
  MODE_TO_TIER,
  MODES_BY_TIER,
  MODE_NAMES_BY_SCALE,
  SCALE_TYPES,
  MODE_NAMES,
  SCALE_TYPES_BY_TIER,
} from '../../data/contentStore';

export {
  MODES,
  MODE_TO_TIER,
  MODES_BY_TIER,
  MODE_NAMES_BY_SCALE,
  SCALE_TYPES,
  MODE_NAMES,
  SCALE_TYPES_BY_TIER,
};

// ── Constantes + fonctions non-contenu (restent dans modes.js) ──
export {
  MODE_ALIASES,
  getMode,
  getAllModeNames,
  getModeIntervals,
  getModeInfo,
  SCALE_TIERS,
  SCALE_TIER_LABELS,
  SCALE_TIER_DESCRIPTIONS,
  getTierForMode,
  getTierForScaleType,
  SCALE_DIFFICULTY_ORDER,
  SCALE_DIFFICULTY_LABELS,
  getScaleDifficulty
} from './modes';

// ── Moods (donnees vives re-exportees par moodMappings.js) ──
export {
  MOOD_MAPPINGS,
  MOOD_TO_SCALE,
  ALL_MOODS,
  getAllMoods,
  getPrimaryScaleForMood,
  getAllScalesForMood,
  getMoodsForScale,
  getPrimaryMoodMappings
} from './moodMappings';

const _MODES_BY_TIER = MODES_BY_TIER;

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS (operent sur le miroir vif)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the scale type for a given mode name
 */
export const getScaleTypeForMode = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.scaleType : null;
};

/**
 * Get all modes for a given scale type
 */
export const getModesForScaleType = (scaleType) => {
  return MODE_NAMES_BY_SCALE[scaleType] || [];
};

/**
 * Get all available scale types
 */
export const getAvailableScaleTypes = () => SCALE_TYPES;

/**
 * Get modes belonging to a tier.
 */
export const getModesByTier = (tier) => _MODES_BY_TIER[tier] || [];

/**
 * Get mode names of a given scale type, filtered to a tier.
 */
export const getModesForScaleTypeInTier = (scaleType, tier) => {
  const modesOfScale = MODE_NAMES_BY_SCALE[scaleType] || [];
  const modesOfTier = new Set(_MODES_BY_TIER[tier] || []);
  return modesOfScale.filter(name => modesOfTier.has(name));
};

/**
 * Check if a mode is pentatonic
 */
export const isPentatonicMode = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.isPentatonic === true : false;
};

/**
 * Check if a mode should use flats for display
 */
export const modeUsesFlats = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.useFlats === true : false;
};

/**
 * Get the default progression for a mode
 */
export const getDefaultProgressionKey = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.defaultProgressionKey : null;
};

/**
 * Get all progressions for a mode
 */
export const getProgressionsForMode = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.progressions || [] : [];
};

/**
 * Get chord qualities for a mode
 */
export const getChordQualitiesForMode = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.chordQualities || [] : [];
};

/**
 * Get color degrees (characteristic notes) for a mode
 */
export const getColorDegreesForMode = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.colorDegrees || [] : [];
};

/**
 * Get related modes for a mode
 */
export const getRelatedModes = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.relatedModes || [] : [];
};

/**
 * Get mode aliases
 */
export const getModeAliases = (modeName) => {
  const mode = MODES[modeName];
  return mode ? mode.aliases || [] : [];
};

// ═══════════════════════════════════════════════════════════════════════════
// VALIDATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Validate all mode data for integrity
 */
export const validateModeData = () => {
  const errors = [];
  const warnings = [];

  Object.entries(MODES).forEach(([modeName, modeData]) => {
    const requiredFields = ['intervals', 'chordQualities', 'scaleType', 'description'];
    requiredFields.forEach(field => {
      if (!modeData[field]) {
        errors.push(`${modeName}: missing required field '${field}'`);
      }
    });

    if (modeData.intervals && modeData.chordQualities) {
      if (modeData.intervals.length !== modeData.chordQualities.length) {
        errors.push(
          `${modeName}: intervals (${modeData.intervals.length}) and chordQualities (${modeData.chordQualities.length}) length mismatch`
        );
      }
    }

    if (modeData.intervals) {
      const invalidIntervals = modeData.intervals.filter(i => i < 0 || i > 11);
      if (invalidIntervals.length > 0) {
        errors.push(`${modeName}: invalid intervals [${invalidIntervals.join(', ')}]`);
      }
    }

    if (!modeData.progressions || modeData.progressions.length === 0) {
      warnings.push(`${modeName}: no progressions defined`);
    }

    if (!modeData.tags || modeData.tags.length === 0) {
      warnings.push(`${modeName}: no tags defined`);
    }
  });

  return {
    errors,
    warnings,
    isValid: errors.length === 0
  };
};

/**
 * Run validation in development mode
 */
export const runValidationInDev = () => {
  if (process.env.NODE_ENV === 'development') {
    const { errors, warnings, isValid } = validateModeData();

    if (!isValid) {
      console.error('[MusicTheory] Data validation errors:');
      errors.forEach(err => console.error(`  - ${err}`));
    }

    if (warnings.length > 0) {
      console.warn('[MusicTheory] Data validation warnings:');
      warnings.forEach(warn => console.warn(`  - ${warn}`));
    }

    if (isValid && warnings.length === 0) {
      console.log('[MusicTheory] Data validation passed');
    }
  }
};
